<template>
  <div class="bingo-card" @click="toggleCompletion">
    <h3 class="task-header">Zadanie {{ card.id + 1 }}</h3>
    <div class="task">
      <p class="task-description">{{ card.value }}</p>
    </div>
    <Transition name="fade">
      <div v-if="isCompleted" class="overlay">X</div>
    </Transition>
  </div>
  <ConfirmDialog
    :group="`bingo-card-dialog-${card.id}`"
    :style="dialogColors"
    :closable="false"
    :breakpoints="{ '600px': '80vw', '400px': '95vw', '300px': '100vw' }"
  >
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="card-dialog">
        <span class="card-dialog-header">{{ message.header }}</span>
        <div class="card-dialog-buttons">
          <button
            class="card-dialog-buttons-common card-dialog-buttons-reject"
            @click="rejectCallback"
          >
            Nie
          </button>
          <button
            class="card-dialog-buttons-common card-dialog-buttons-accept"
            @click="acceptCallback"
          >
            Tak, anuluj
          </button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue';
import { useStateStore } from '@/stores/state.store';
import { useBingoStore } from '@/stores/bingo/bingo.store';
import { Card } from '@/stores/bingo/bingo.state';
import { useConfirm } from 'primevue/useconfirm';
import { useDialog } from 'primevue/usedialog';
const SenderDialog = defineAsyncComponent(
  () => import('../sender/SenderDialog.vue'),
);

const confirm = useConfirm();
const dialog = useDialog();

const stateStore = useStateStore();
const bingoStore = useBingoStore();

const props = defineProps<{
  card: Card;
}>();

const emits = defineEmits<{ (_e: 'toggleError'): void }>();

const isCompleted = computed(() => {
  return bingoStore.getBingoCards[props.card.id]?.isCompleted ?? false;
});

const toggleCompletion = () => {
  if (props.card.isCompleted) {
    if (!bingoStore.isEndGame) {
      confirmCancelTask(props.card.id);
    }
  } else {
    if (bingoStore.playerName === '') {
      emits('toggleError');
      return;
    }

    if (bingoStore.isFileRequired) {
      showSender();
    } else {
      bingoStore.toggleCardCompletion(props.card.id);
    }
  }
};

const cancelTask = (cardId: number) => {
  if (!bingoStore.isEndGame) {
    bingoStore.toggleCardCompletion(cardId, false);
  }
};

const mainColor = ref(stateStore.colors.mainColor);
const backgroundColor = ref(stateStore.colors.backgroundColor);
const borderColor = ref(stateStore.colors.mainColor);

const confirmCancelTask = (cardId: number): void => {
  confirm.require({
    header: 'Czy na pewno chcesz anulować wykonane zadanie?',
    group: `bingo-card-dialog-${props.card.id}`,
    accept: () => cancelTask(cardId),
    reject: () => {},
    defaultFocus: 'reject',
  });
};

const dialogColors = {
  '--main-color': mainColor.value,
  '--background-color': backgroundColor.value,
  '--border-color': mainColor.value,
  '--p-dialog-border-color': mainColor.value,
  '--p-dialog-background': backgroundColor.value,
};

const showSender = () => {
  dialog.open(SenderDialog, {
    props: {
      header: 'Wyślij zdjęcie z wykonanego zadania',
      style: {
        color: mainColor.value,
        background: backgroundColor.value,
        'border-color': mainColor.value,
        width: '50vw',
        '--p-button-text-secondary-color': mainColor.value,
        '--p-button-text-secondary-hover-background': backgroundColor.value,
        '--p-button-text-secondary-active-background': backgroundColor.value,
        '--p-button-primary-border-color': mainColor.value,
        '--p-button-primary-hover-border-color': mainColor.value,
        '--p-button-primary-active-border-color': mainColor.value,
        '--p-button-primary-hover-background': backgroundColor.value,
        '--p-button-primary-active-background': backgroundColor.value,
        '--p-button-secondary-border-color': mainColor.value,
        '--p-button-secondary-hover-border-color': mainColor.value,
        '--p-button-secondary-active-border-color': mainColor.value,
        '--p-button-secondary-hover-background': backgroundColor.value,
        '--p-button-secondary-active-background': backgroundColor.value,
        '--p-button-secondary-background': backgroundColor.value,
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      modal: true,
      draggable: false,
    },
    data: {
      mainColor: mainColor.value,
      buttonColor: mainColor.value,
      backgroundColor: backgroundColor.value,
    },
    onClose: (opt) => {
      const callbackParams = opt?.data;
      if (callbackParams && callbackParams.isSended) {
        bingoStore.toggleCardCompletion(props.card.id);
      }
    },
  });
};
</script>

<style scoped lang="scss">
@import '@/assets/styleConstans';

.bingo-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  border: 2px solid v-bind(borderColor);
  padding: 10px;
  padding-bottom: 15px;
  border-radius: 5px;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s;
  color: v-bind(mainColor);
  background-color: v-bind(backgroundColor);
}

.bingo-card:hover {
  transform: scale(1.05);

  @media (max-width: $media-phone) {
    transform: none;
  }
}

.task-header {
  font-size: 2em;

  @media (max-width: $media-phone) {
    line-height: 1.3em;
    font-size: 1em;
  }
}

.task {
  flex: 1 0 auto;
  display: flex;
  justify-content: center;
  gap: 5px;
  padding: 0 10px 10px;

  @media (max-width: $media-phone) {
    padding: 0;
  }
}

.task-description {
  font-size: 1.4em;
  height: 100%;
  text-align: center;
  display: flex;
  align-items: center;

  @media (max-width: $media-phone) {
    font-size: 0.7em;
  }
}

.overlay {
  position: absolute;
  inset: 0;
  background-color: v-bind(mainColor);
  opacity: 0.6;
  color: v-bind(backgroundColor);
  font-size: 10em;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.card-dialog {
  display: flex;
  flex-direction: column;
  padding: 15px 25px;
  gap: 25px;
  justify-content: center;
  align-items: center;
  color: var(--main-color);
  background-color: var(--background-color);
  border-radius: 12px;

  @media (max-width: $media-phone) {
    padding: 15px 20px;
    gap: 20px;
  }

  &-header {
    font-weight: 700;
    font-size: 1.5em;
    line-height: 1.25em;
    text-align: center;
    max-width: 400px;
  }

  &-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: auto;
    width: 70%;

    @media (max-width: $media-phone) {
      width: 75%;
    }

    &-common {
      border: 1px solid var(--main-color);
      border-radius: 10px;
      padding: 5px 15px;
      cursor: pointer;
      font-size: 1.2em;
      transition: all ease 0.7s;
    }

    &-accept {
      color: var(--main-color);
      background-color: var(--background-color);

      &:hover {
        color: var(--background-color);
        background-color: var(--main-color);
      }
    }

    &-reject {
      color: var(--background-color);
      background-color: var(--main-color);

      &:hover {
        color: var(--main-color);
        background-color: var(--background-color);
      }
    }
  }
}

.fade-enter-active {
  transition: opacity 1.5s ease;
}

.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
